// src/shared/changelog/autoOpenStore.ts
import type { AutoOpenRecord } from "./autoOpenRules";

/**
 * localStorage key holding the last auto-open of the What's new modal — when it
 * happened and which release it showed (§2.5).
 *
 * Per device and outside Redux's `persist`, like every other changelog key. It
 * stays separate from `SeenStore` on purpose: that seam exists because §2.4
 * leaves the door open to moving the *seen* state to the server, where it would
 * follow the user across devices. This is scheduling — how often a **browser**
 * may interrupt — and it belongs to the device whatever happens to the rest.
 */
export const LAST_AUTO_OPEN_KEY = "ctx:changelog:lastAutoOpenAt";

function isRecord(value: unknown): value is AutoOpenRecord {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Partial<AutoOpenRecord>;
  return typeof candidate.at === "string" && typeof candidate.releaseId === "string";
}

/**
 * The last auto-open, or `null` when there has not been one.
 *
 * Anything unreadable is `null` — "never auto-opened". The failure shows itself
 * as one dialog the reader can close, which is recoverable; the other direction
 * would silently retire the feature on that device with nothing to notice.
 */
export function readLastAutoOpen(): AutoOpenRecord | null {
  try {
    const raw = window.localStorage.getItem(LAST_AUTO_OPEN_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Records an auto-open. Only the automatic kind — a click is not one (§2.1). */
export function recordAutoOpen(releaseId: string, now: number = Date.now()): void {
  try {
    const record: AutoOpenRecord = {
      at: new Date(now).toISOString(),
      releaseId,
    };
    window.localStorage.setItem(LAST_AUTO_OPEN_KEY, JSON.stringify(record));
  } catch {
    // Ignore — a device that cannot record this gets one more auto-open than
    // the rules allow, which is the mild failure. Refusing to open would be the
    // loud one.
  }
}
